import {
  clampMenuToViewport,
  ContextMenuItem,
  ContextMenuSurface,
} from '@src/components/common/contextMenuStyles';
import { TOP_LAYER_VARS } from '@src/components/common/topLayerZ';
import { CLIP_PREVIEW_Z } from '@src/components/content-components/clipPreviewZ';
import DeleteIcon from '@src/svgs/DeleteIcon';
import SimplePencilIcon from '@src/svgs/SimplePencilIcon';
import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';

import { useOutsideAlerter } from './useClickOutside';

/**
 * PLAT-12034 — a delivery marker drawn inside a dubbed line's words.
 *
 * The dub recorded this line with `[whispers]` written before a word; this is
 * that marker where it sits, as a chip among the words, so a reviewer can see
 * how a line was directed while reading it. Gold, because it is a creative
 * control (never green, which is for actions); `vertical-align: middle` for
 * the reason `TimelineMarkerChip`'s base gives; `contentEditable={false}` and
 * `user-select: none` so it is neither typed into nor copied with the words.
 *
 * Clicking it offers two things, in a small menu on the body: **Edit
 * Markers…**, which the host opens the line's markers editor for, and
 * **Remove**, which re-dubs the line without this marker. Neither touches the
 * translation text.
 */
export interface DeliveryMarkerChipProps {
  /** The text inside the brackets: `whispers`. */
  tag: string;
  /** Open the line's markers editor, anchored to this chip. Absent ⇒ no row. */
  onEdit?: (anchor: HTMLElement) => void;
  /** Re-dub the line without this marker. Absent ⇒ no row. */
  onRemove?: () => void;
  /** True while the line is being re-dubbed: the chip shows, the menu does not. */
  busy?: boolean;
}

const MENU_WIDTH = 200;
const ROW_HEIGHT = 40;
const MENU_PADDING = 4;

const DeliveryMarkerChip: React.FC<DeliveryMarkerChipProps> = ({
  tag,
  onEdit,
  onRemove,
  busy = false,
}) => {
  const chipRef = useRef<HTMLSpanElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menu, setMenu] = useState<{ left: number; top: number } | null>(null);
  const interactive = !busy && !!(onEdit || onRemove);

  useOutsideAlerter(menuRef, () => setMenu(null));
  useEffect(() => {
    if (!menu) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      setMenu(null);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [menu]);

  const open = (e: React.MouseEvent) => {
    if (!interactive) return;
    // The phrase under the chip seeks the player on click; the chip is its own
    // control.
    e.stopPropagation();
    e.preventDefault();
    const anchor = chipRef.current;
    if (!anchor) return;
    const rows = (onEdit ? 1 : 0) + (onRemove ? 1 : 0);
    setMenu(
      placeBelow(anchor, MENU_WIDTH, rows * ROW_HEIGHT + MENU_PADDING * 2),
    );
  };

  return (
    <>
      <Chip
        ref={chipRef}
        contentEditable={false}
        suppressContentEditableWarning
        role={interactive ? 'button' : undefined}
        tabIndex={interactive ? 0 : undefined}
        aria-haspopup={interactive ? 'menu' : undefined}
        aria-expanded={interactive ? !!menu : undefined}
        aria-label={`Delivery marker: ${tag}`}
        title={`Delivery marker · ${tag}`}
        data-testid="delivery-marker-chip"
        data-tag={tag}
        $interactive={interactive}
        onClick={open}
        onKeyDown={e => {
          if (!interactive) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.stopPropagation();
            const anchor = chipRef.current;
            if (!anchor) return;
            const rows = (onEdit ? 1 : 0) + (onRemove ? 1 : 0);
            setMenu(
              placeBelow(
                anchor,
                MENU_WIDTH,
                rows * ROW_HEIGHT + MENU_PADDING * 2,
              ),
            );
          }
        }}
        // Never let the mousedown start a text selection or seek.
        onMouseDown={e => e.preventDefault()}
      >
        {tag}
      </Chip>
      {menu &&
        ReactDOM.createPortal(
          <Surface
            ref={menuRef}
            placement={menu}
            onMouseDown={e => e.preventDefault()}
            role="menu"
            data-testid="delivery-marker-menu"
          >
            {onEdit && (
              <ContextMenuItem
                type="button"
                role="menuitem"
                data-testid="delivery-marker-edit"
                onClick={e => {
                  e.stopPropagation();
                  setMenu(null);
                  if (chipRef.current) onEdit(chipRef.current);
                }}
              >
                <SimplePencilIcon />
                Edit Markers…
              </ContextMenuItem>
            )}
            {onRemove && (
              <ContextMenuItem
                type="button"
                role="menuitem"
                $danger
                data-testid="delivery-marker-remove"
                onClick={e => {
                  e.stopPropagation();
                  setMenu(null);
                  onRemove();
                }}
              >
                <DeleteIcon strokeColor="currentColor" />
                Remove
              </ContextMenuItem>
            )}
          </Surface>,
          document.body,
        )}
    </>
  );
};

export default DeliveryMarkerChip;

function placeBelow(
  anchor: HTMLElement,
  width: number,
  height: number,
): { left: number; top: number } {
  const rect = anchor.getBoundingClientRect();
  const MARGIN = 6;
  const openAbove = rect.bottom + MARGIN + height > window.innerHeight;
  const top = openAbove ? rect.top - height - MARGIN : rect.bottom + MARGIN;
  const clamped = clampMenuToViewport(rect.left, top, width, height);
  return { left: clamped.x, top: clamped.y };
}

const Chip = styled.span<{ $interactive: boolean }>`
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin: 0 3px;
  padding: 2px 6px;
  border-radius: 3px;
  font-size: 11px;
  line-height: 1;
  font-weight: var(--font-weight-medium);
  white-space: nowrap;
  user-select: none;
  color: var(--accent);
  background: color-mix(in srgb, var(--accent) 14%, transparent);
  border: 1px solid color-mix(in srgb, var(--accent) 35%, transparent);
  cursor: ${p => (p.$interactive ? 'pointer' : 'default')};

  ${p =>
    p.$interactive
      ? `
        &:hover {
          background: color-mix(in srgb, var(--accent) 24%, transparent);
        }
        &:focus-visible {
          outline: 2px solid var(--accent);
          outline-offset: 1px;
        }
      `
      : ''}
`;

const Surface = styled(ContextMenuSurface).attrs(
  (props: { placement: { left: number; top: number } }) => props,
)`
  position: fixed;
  left: ${p => p.placement.left}px;
  top: ${p => p.placement.top}px;
  z-index: var(
    ${TOP_LAYER_VARS.contextMenu},
    ${CLIP_PREVIEW_Z.translationEditorPopover}
  );
`;
